import { useEffect, useMemo, useState } from 'react';
import useMenus from '../context/MenuContext';
import useParam from '../context/ParamContext';
import useUsers from '../context/UsersContext';
import masterDataService from '../service/masterData.service';
import { getResponsibleList } from '../utils/getResponsibleList';
import { generateAutocompleteItems } from '../utils/helper';
import jokerService from '../service/joker.service';
import { JokerTypes } from '../config/joker';

export const useProjectTasks = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit, getSubMenuNamesByUrls } = useMenus();
  const { getParam, params } = useParam();
  const { getUser, user } = useUsers();

  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [projectTaskSubmenu, setProjectTaskSubmenu] = useState(null);
  const [canEdit, setCanEdit] = useState(true);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteData, setAutocompleteData] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    user: [],
    userGroup: [],
    type: [],
    status: [],
    brigade: [],
    resourceOne: [],
    resourceThree: [],
    resourceFour: [],
  });
  const [loading, setLoading] = useState(true);
  const [jokerFields, setJokerFields] = useState([]);
  const [jokerLoading, setJokerLoading] = useState(true);

  const simpleTaskSubmenuId = getMenuSubMenuId('simpleTask');
  const resourceOneSubmenuId = getMenuSubMenuId('resourceOne');

  useEffect(() => {
    getMenus();
    getParam();
    if (!Object.keys(user || {}).length) getUser();
    getJokerFields();
  }, []);

  useEffect(() => {
    const projectTasksSubmenuId = getMenuSubMenuId('projectTasks');
    setCanEdit(getCanEdit(projectTasksSubmenuId));
    setProjectTaskSubmenu(projectTasksSubmenuId);
  }, [menus]);

  const showBrigadeManagement = params.TASKLIST_SHOW_BRIGADE_MANAGEMENT === 'Y';
  const showResourceThreeField = params.TASKLIST_SHOW_RESOURCETHREE_FIELD === 'Y';
  const showResourceFourField = params.TASKLIST_SHOW_RESOURCEFOUR_FIELD === 'Y';

  useEffect(() => {
    if (!menus?.length || !Object.keys(params).length) return;

    initDefaultValues();
  }, [simpleTaskSubmenuId, resourceOneSubmenuId, showBrigadeManagement, showResourceThreeField, showResourceFourField]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['resourceThree', 'resourceFour']);
  }, [menus]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      simpleTaskSubmenuId ? masterDataService.typeToForm(simpleTaskSubmenuId) : Promise.resolve({ typeItems: [] })
    );

    promiseArray.push(
      simpleTaskSubmenuId ? masterDataService.statusToForm(simpleTaskSubmenuId) : Promise.resolve({ statusItems: [] })
    );

    promiseArray.push(getResponsibleList());

    promiseArray.push(
      showBrigadeManagement && resourceOneSubmenuId
        ? masterDataService.statusToForm(resourceOneSubmenuId)
        : Promise.resolve({ statusItems: [] })
    );

    promiseArray.push(showBrigadeManagement ? masterDataService.resourceOneToForm() : Promise.resolve([]));

    promiseArray.push(showResourceThreeField ? masterDataService.resourceThreeToForm() : Promise.resolve([]));

    promiseArray.push(showResourceFourField ? masterDataService.resourceFourToForm() : Promise.resolve([]));

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          user: resp[1],
          userGroup: resp[2],
          type: resp[3],
          status: resp[4],
          responsible: resp[5],
          brigade: resp[6],
          resourceOne: resp[7],
          resourceThree: resp[8],
          resourceFour: resp[9],
        };
        setAutocompleteData(aData);

        const filteredR1 = aData.resourceOne.filter((r1) => r1.active);
        const filteredR3 = aData.resourceThree.filter((r3) => r3.active);
        const filteredR4 = aData.resourceFour.filter((r4) => r4.active);

        const acOptions = {
          project: generateAutocompleteItems(aData.project, ['projectName', 'orderNumber'], 'projectId'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          userGroup: generateAutocompleteItems(aData.userGroup, 'name', 'id'),
          type: generateAutocompleteItems(aData.type.typeItems, 'itemName', 'itemId'),
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
          responsible: aData.responsible,
          brigade: generateAutocompleteItems(aData.brigade.statusItems, 'itemName', 'itemId'),
          resourceOne: generateAutocompleteItems(filteredR1, 'name', 'id'),
          resourceThree: generateAutocompleteItems(filteredR3, 'name', 'id'),
          resourceFour: generateAutocompleteItems(filteredR4, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getJokerFields = () => {
    jokerService
      .getFields(JokerTypes.TASKLIST)
      .then((data) => {
        setJokerFields(data);
      })
      .finally(() => {
        setJokerLoading(false);
      });
  };

  return {
    refetchTable,
    setRefetchTable,
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    canEdit,
    rowSelectionModel,
    setRowSelectionModel,
    autocompleteData,
    setAutocompleteData,
    autocompleteOptions,
    setAutoCompleteOptions,
    loading,
    setLoading,
    projectTaskSubmenu,
    jokerLoading,
    jokerFields,
    submenuNames,
  };
};

export default useProjectTasks;
